<template>
<!-- eslint-disable max-len -->
<div class="metrics" :style="metricsStyle">
  <!--
    Never use v-html with end-user generated content, because it's a vector for XSS attacks
    This is what the following line used to be:
    <h2 id="header-1">{{ activeEnvironment.resultScreenHeader || 'COMPANY DASHBOARD' }}</h2>
  -->
  <h2 id="header-1" v-html="activeEnvironment.resultScreenHeader || 'COMPANY DASHBOARD'"></h2>

  <div class="metric-wheels-scroll-view" :style="metricWheelsScrollViewStyle">
    <div :style="verticalScrollBoundWrapperStyle"> <!-- hack to get the scroll bounds to work -->
      <div id="metric-wheels"> <!-- dynamic height, static width, wrapping flex box -->
        <radial-progress v-for="({ metric, value }, i) in visibleCurrentMetrics" :key="`metric-${i}`"
          :value="value"
          :enable-animation-tick-sound="i < 1"
          :percent="!!metric.percentage"
          :integer="!!metric.integer"
          :min="metric.min"
          :max="metric.max"
          :color="colors[i % colors.length]"
          :name="metric.title"
        ></radial-progress>
      </div>
    </div>
  </div>

  <div v-if="showCash">
    <h2 id="header-2">Remaining budget</h2>
    <p class="cash-box">
      <span class="currency">{{ currentCash | currency }}</span>
    </p>
  </div>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import RadialProgress from './RadialProgress';

export default {
  props: {
    animate: { type: Boolean },
    showCash: { type: Boolean, default: true }
  },
  components: {
    RadialProgress
  },
  mounted() {
    this.updateMetrics();
    if (this.activeEnvironment != null && this.activeEnvironment.metricGraphBarColors != null) {
      const parsed = JSON.parse(this.activeEnvironment.metricGraphBarColors);
      if (parsed.length > 0) {
        this.colors = parsed;
      }
    }
  },
  data() {
    return {
      colors: ['dodgerblue', 'gold', 'mediumvioletred', 'gold', 'mediumvioletred', 'dodgerblue'],
      currentCash: 0,
      currentMetrics: []
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'displayTeamDecisionBeginMetrics',
      'displayMetrics',
      'teamDecisionBeginCash',
      'cash',
      'displayCash',
      'currentState',
      'resultsState',
      'complete',
      'lastTeamDecision',
      'teamDecision',
      'group'
    ]),
    visibleCurrentMetrics() {
      return this.currentMetrics.filter(({ metric, hidden }) => {
        if (!hidden && metric && !metric.hidden) {
          if (this.currentState === this.resultsState && !this.complete
            && this.lastTeamDecision != null && this.lastTeamDecision.hiddenTeamMetrics != null) {
            return this.lastTeamDecision.hiddenTeamMetrics.every(hiddenMetric => (
              (hiddenMetric.showInResults != null && hiddenMetric.showInResults)
                || hiddenMetric._id !== metric._id
            ));
          }
          if (this.teamDecision != null && this.teamDecision.hiddenTeamMetrics != null) {
            return this.teamDecision.hiddenTeamMetrics.every(hiddenMetric => (
              (hiddenMetric.showInResults != null && hiddenMetric.showInResults)
                || hiddenMetric._id !== metric._id
            ));
          }
          return true;
        }
        return false;
      });
    },
    metricsStyle() {
      return {
        height: `${((this.showCash) ? 145 : 0) + ((this.visibleCurrentMetrics.length > 3) ? 515 : 315)}px`
      };
    },
    metricWheelsScrollViewStyle() {
      return {
        height: `${(this.visibleCurrentMetrics.length > 3) ? 430 : 230}px`
      };
    },
    verticalScrollBoundWrapperStyle() {
      return {
        overflow: 'hidden',
        height: `${230 + (200 * (Math.ceil(this.visibleCurrentMetrics.length / 3) - 1))}px`
      };
    }
  },
  methods: {
    ...mapActions([
      'setTeamDecisionBeginMetrics'
    ]),
    async updateMetrics() {
      if (this.animate) {
        // cash animation
        await new Promise(resolve => setTimeout(resolve, 1000));
        const startCash = this.teamDecisionBeginCash;
        const endCash = this.cash;
        const increase = endCash >= startCash;
        const steps = 100;
        const duration = 1500;
        const step = (endCash - startCash) / steps;
        const cashInterval = setInterval(() => {
          if (increase) {
            if (this.currentCash >= endCash) return clearInterval(cashInterval);
            this.currentCash = Math.min(endCash, Math.ceil(this.currentCash + step));
          } else {
            if (this.currentCash <= endCash) return clearInterval(cashInterval);
            this.currentCash = Math.max(endCash, Math.floor(this.currentCash + step));
          }
        }, duration / steps);
      }

      // this triggers the metric animations if any of the new values are different...
      // the actual animation is in a watcher in RadialProgress.vue
      this.currentMetrics = this.displayMetrics;

      await this.setTeamDecisionBeginMetrics();
    }
  },
  watch: {
    group(newValue, oldValue) {
      // animate again each time the group is updated
      // this happens even if no metrics changed because the animation logic handles that check
      if (newValue && (!oldValue || (newValue.updated !== oldValue.updated))) {
        // eslint-disable-next-line max-len
        this.currentMetrics = this.animate ? this.displayTeamDecisionBeginMetrics : this.displayMetrics;
        this.currentCash = this.animate ? this.teamDecisionBeginCash : this.cash;
        this.updateMetrics();
      }
    }
  },
  created() {
    this.currentMetrics = this.animate ? this.displayTeamDecisionBeginMetrics : this.displayMetrics;
    this.currentCash = this.animate ? this.teamDecisionBeginCash : this.cash;
  }
};
</script>

<style lang="scss" scoped>
.metrics {
  position: absolute;
  background-color: var(--primaryColor);
  width: 780px;
  min-height: 460px;
  max-height: 660px;
}
h2 {
  position: absolute;
  color: white;
  font-weight: bold;
  font-size: 30px;
  margin: 0;
}
#header-1 {
  top: 20px;
  left: 20px;
}
.metric-wheels-scroll-view {
  overflow-y: auto;
  overflow-x: hidden;
  background-color: transparent;
  width: 770px;
  max-height: 230px;
  max-height: 430px;
  left: 12px;
  position: absolute;
  top: 80px;
}
#metric-wheels {
  background-color: transparent;
  min-height: 230px;
  width: 770px;
}
#header-2 {
  bottom: 120px;
  left: 20px;
  text-transform: uppercase;
}
.cash-box {
  background-color: white;
  color: black;
  margin: 0;
  bottom: 20px;
  left: 20px;
  right: 20px;
  height: 90px;
  position: absolute;
  font-size: 68px;
  font-weight: bold;
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
}
.currency::before {
  font-size: 58px;
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/Metrics.vue